import { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import NoTableEntries from "@components/DataTable/NoTableEntries";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer.tsx";
import { useAuthStore } from "@persisted-state/auth/auth.store.ts";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import AddMovementServiceButton from "../components/AddMovementServiceButton";
import CurrentMovements from "./CurrentMovements";
import PastMovements from "./PastMovements";
import { MOVEMENT_SERVICES_FIELDS } from "./utils";

const LANG_BASE = "vessel-call.movement-services";

type Header = {
  field: string;
  columnName: string;
  minWidth?: number;
};

const Movements = () => {
  const { t } = useTranslation();
  const { userActionPermissions } = useAuthStore();
  const {
    handleOpenDrawer,
    currentMovements,
    pastMovementsData,
    isPastMovementsLoading,
    isCurrentMovementsLoading,
    isMovementSubmitting,
    isMovementTransitioning,
    ownersMatter,
    proposedAgency,
    pastMovementsPage,
    pastMovementsPageSize,
    setPastMovementsPagination,
  } = useOutletContext<MovementOutletContext>();
  const headers: Header[] = useMemo(
    () =>
      Object.keys(MOVEMENT_SERVICES_FIELDS).map((key) => ({
        field: key.toString(),
        columnName: MOVEMENT_SERVICES_FIELDS[key as keyof typeof MOVEMENT_SERVICES_FIELDS],
      })),
    [],
  );

  const pastMovements = pastMovementsData?.items || [];
  const totalPasMovementsCount = pastMovementsData?.totalElements || 0;
  const isLoading =
    isCurrentMovementsLoading ||
    isPastMovementsLoading ||
    isMovementSubmitting ||
    isMovementTransitioning;
  const hasMovements = currentMovements?.length || pastMovements.length;

  const userHasRequestPermission =
    checkPermission(userActionPermissions, "canRequestMovementService") &&
    !ownersMatter &&
    !proposedAgency;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-8">
          <CircularProgress />
        </div>
      );
    }

    if (hasMovements) {
      return (
        <>
          <CurrentMovements
            langBase={`${LANG_BASE}`}
            rows={currentMovements}
            headers={headers}
            openDrawer={handleOpenDrawer}
          />
          {pastMovements.length ? (
            <PastMovements
              rows={pastMovements}
              headers={headers}
              langBase={LANG_BASE}
              totalCount={totalPasMovementsCount}
              page={pastMovementsPage}
              pageSize={pastMovementsPageSize}
              onPageChange={setPastMovementsPagination}
            />
          ) : null}
        </>
      );
    }

    return (
      <>
        <NoTableEntries
          noMargin
          customMessagePrefix="vessel-call.movement-services.empty-table-title"
          dataName={t("vessel-call.vessel-status.movement-services")}
          actionName={userHasRequestPermission ? t(`${LANG_BASE}.add-service`) : ""}
          descriptionComponents={{ strong: <strong /> }}
        />
        <div className="flex justify-center mt-4">
          <AddMovementServiceButton
            langBase={LANG_BASE}
            ownersMatter={ownersMatter}
            proposedAgency={proposedAgency}
          />
        </div>
      </>
    );
  };

  return <div>{renderContent()}</div>;
};

export default Movements;
